import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface PodcastHost {
  id: string;
  brand_id: string;
  name: string;
  slug?: string | null;
  title?: string | null;
  bio?: string | null;
  email?: string | null;
  image_url?: string | null;
  social_links?: {
    linkedin?: string;
    twitter?: string;
    website?: string;
    github?: string;
  } | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type PodcastHostInsert = Omit<PodcastHost, 'id' | 'created_at' | 'updated_at'>;
export type PodcastHostUpdate = Partial<Omit<PodcastHost, 'id' | 'created_at' | 'updated_at'>>;

const syncHostToContacts = async (
  brandId: string,
  host: { name: string; email?: string | null; phone?: string | null }
): Promise<string | null> => {
  if (!host.email) return null;
  
  const nameParts = host.name.trim().split(/\s+/);
  const firstName = nameParts[0] || host.name;
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
  
  try {
    const { data: contactId, error } = await supabase.rpc('upsert_os_contact', {
      p_brand_id: brandId,
      p_email: host.email,
      p_first_name: firstName,
      p_last_name: lastName,
      p_phone: host.phone || null,
      p_module: ['Podcast Host'],
      p_activity: ['host'],
      p_organization: null,
      p_title: null,
      p_user_id: null,
    });
    
    if (error) {
      console.error('Error syncing host to contacts:', error);
      return null;
    }
    
    return contactId;
  } catch (error) {
    console.error('Error syncing host to contacts:', error);
    return null;
  }
};

export function usePodcastHosts(brandId: string | null) {
  const queryClient = useQueryClient();

  const { data: hosts, isLoading } = useQuery({
    queryKey: ['podcast-hosts', brandId],
    queryFn: async () => {
      if (!brandId) return [];

      const { data, error } = await supabase
        .from('os_podcast_shows_hosts')
        .select('*')
        .eq('brand_id', brandId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data as PodcastHost[];
    },
    enabled: !!brandId,
  });

  const createHost = useMutation({
    mutationFn: async (host: PodcastHostInsert) => {
      // Sync to contacts first
      const contactId = await syncHostToContacts(host.brand_id, {
        name: host.name,
        email: host.email,
        phone: null,
      });
      
      const { data, error } = await supabase
        .from('os_podcast_shows_hosts')
        .insert([{ ...host, contact_id: contactId }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['podcast-hosts', brandId] });
      toast.success('Host created successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to create host', { description: error.message });
    },
  });

  const updateHost = useMutation({
    mutationFn: async ({ id, ...updates }: PodcastHostUpdate & { id: string }) => {
      // Get current host for brand_id and contact sync
      const { data: currentHost } = await supabase
        .from('os_podcast_shows_hosts')
        .select('brand_id, name, email')
        .eq('id', id)
        .single();
      
      let contactId: string | null = null;
      if (currentHost) {
        contactId = await syncHostToContacts(currentHost.brand_id, {
          name: updates.name || currentHost.name,
          email: updates.email || currentHost.email,
          phone: null,
        });
      }
      
      const { data, error } = await supabase
        .from('os_podcast_shows_hosts')
        .update({ ...updates, ...(contactId ? { contact_id: contactId } : {}) })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['podcast-hosts', brandId] });
      toast.success('Host updated successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to update host', { description: error.message });
    },
  });

  const deleteHost = useMutation({
    mutationFn: async (hostId: string) => {
      const { error } = await supabase
        .from('os_podcast_shows_hosts')
        .update({ is_active: false })
        .eq('id', hostId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['podcast-hosts', brandId] });
      toast.success('Host deactivated successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to deactivate host', { description: error.message });
    },
  });

  const importFromInstructors = useMutation({
    mutationFn: async (instructorIds: string[]) => {
      if (!brandId) throw new Error('Brand ID required');

      const { data: instructors, error: fetchError } = await supabase
        .from('os_lms_instructors')
        .select('*')
        .in('id', instructorIds)
        .eq('is_active', true);

      if (fetchError) throw fetchError;
      if (!instructors || instructors.length === 0) {
        throw new Error('No instructors found');
      }

      // Sync each instructor to contacts and build hosts with contact_id
      const hosts: (PodcastHostInsert & { contact_id?: string | null })[] = await Promise.all(
        instructors.map(async (instructor) => {
          const contactId = await syncHostToContacts(brandId, {
            name: instructor.name,
            email: instructor.email,
            phone: null,
          });
          return {
            brand_id: brandId,
            name: instructor.name,
            slug: instructor.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
            title: instructor.title,
            bio: instructor.bio,
            email: instructor.email,
            image_url: instructor.image_url,
            social_links: instructor.social_links as PodcastHost['social_links'],
            is_active: true,
            contact_id: contactId,
          };
        })
      );

      const { data, error } = await supabase
        .from('os_podcast_shows_hosts')
        .insert(hosts)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['podcast-hosts', brandId] });
      toast.success(`${data?.length || 0} host(s) imported from instructors`);
    },
    onError: (error: any) => {
      toast.error('Failed to import hosts', { description: error.message });
    },
  });

  const importFromAuthors = useMutation({
    mutationFn: async (authorIds: string[]) => {
      if (!brandId) throw new Error('Brand ID required');

      const { data: authors, error: fetchError } = await supabase
        .from('os_article_authors')
        .select('*')
        .in('id', authorIds)
        .eq('is_active', true);

      if (fetchError) throw fetchError;
      if (!authors || authors.length === 0) {
        throw new Error('No authors found');
      }

      // Sync each author to contacts and build hosts with contact_id
      const hosts: (PodcastHostInsert & { contact_id?: string | null })[] = await Promise.all(
        authors.map(async (author) => {
          const contactId = await syncHostToContacts(brandId, {
            name: author.name,
            email: author.email,
            phone: null,
          });
          return {
            brand_id: brandId,
            name: author.name,
            slug: author.slug || author.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
            title: author.title,
            bio: author.bio,
            email: author.email,
            image_url: author.avatar_url,
            social_links: author.social_links as PodcastHost['social_links'],
            is_active: true,
            contact_id: contactId,
          };
        })
      );

      const { data, error } = await supabase
        .from('os_podcast_shows_hosts')
        .insert(hosts)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['podcast-hosts', brandId] });
      toast.success(`${data?.length || 0} host(s) imported from authors`);
    },
    onError: (error: any) => {
      toast.error('Failed to import hosts', { description: error.message });
    },
  });

  return {
    hosts: hosts || [],
    isLoading,
    createHost,
    updateHost,
    deleteHost,
    importFromInstructors,
    importFromAuthors,
  };
}
